import React, { useEffect, useMemo, useState } from "react";
import { Tv, Music2, Baby, Newspaper, Trophy, Gamepad2, Mic2, GraduationCap, Search as MagIcon, ExternalLink, Sparkles, Loader2, RefreshCcw } from "lucide-react";
import { api } from "@/lib/api";
import { useSeo } from "@/hooks/useSeo";
import { CricketPanel } from "@/components/entertainment/CricketPanel";
import { FeedShelf } from "@/components/entertainment/FeedShelf";

// Kinds backed by a real in-app feed; the rest are provider links only.
const FEEDS = { ott: ["shows", "TV shows · airing & streaming"], games: ["games", "Free-to-play games"],
                kids: ["kids", "Children's books"], learn: ["learn", "Learn · science, maths & history books"] };

// -----------------------------------------------------------------------------
// Entertainment Hub — one-tap tiles into every major streaming, OTT, music,
// news, sports, gaming, podcast and learning platform. Includes a global
// search bar that deep-links your query into every provider in the chosen
// kind (via GET /entertainment/ott/search). Layout mirrors a smart-TV home.
// -----------------------------------------------------------------------------
const KINDS = [
  { k: "ott",     l: "Movies & Shows", Icon: Tv,             accent: "from-red-500 to-fuchsia-600" },
  { k: "music",   l: "Music",          Icon: Music2,         accent: "from-emerald-500 to-teal-600" },
  { k: "kids",    l: "Kids",           Icon: Baby,           accent: "from-amber-400 to-orange-500" },
  { k: "news",    l: "News",           Icon: Newspaper,      accent: "from-slate-600 to-slate-800" },
  { k: "sports",  l: "Sports",         Icon: Trophy,         accent: "from-sky-500 to-indigo-600" },
  { k: "games",   l: "Games",          Icon: Gamepad2,       accent: "from-fuchsia-500 to-pink-600" },
  { k: "podcast", l: "Podcasts",       Icon: Mic2,           accent: "from-purple-500 to-violet-700" },
  { k: "learn",   l: "Learning",       Icon: GraduationCap,  accent: "from-emerald-600 to-cyan-700" },
];

// Google's favicon proxy — no API key needed, works for any public homepage.
const logoFor = (home) => {
  try {
    const u = new URL(home);
    return `https://www.google.com/s2/favicons?sz=64&domain=${u.hostname}`;
  } catch { return null; }
};

export default function Entertainment() {
  const [platforms, setPlatforms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [activeKind, setActiveKind] = useState("");
  const [region, setRegion] = useState("global");
  const [feedQ, setFeedQ] = useState("");

  useSeo({
    title: "Entertainment Hub · OTT · Music · Kids · News · find2near",
    description: "Every streaming service in one place — Netflix, Prime Video, Hotstar, JioCinema, Zee5, Sony LIV, MX Player, Spotify, JioSaavn, YouTube, learning and more. Search all of them in one tap.",
    canonical: "/entertainment",
  });

  useEffect(() => {
    setLoading(true);
    api.get("/entertainment/platforms", { params: { region } })
       .then((r) => setPlatforms(r.data.platforms || []))
       .catch(() => setPlatforms([]))
       .finally(() => setLoading(false));
  }, [region]);

  const grouped = useMemo(() => {
    const g = {};
    for (const p of platforms) (g[p.kind] = g[p.kind] || []).push(p);
    return g;
  }, [platforms]);

  const searchAll = () => {
    if (!q.trim()) return;
    // Kinds with a real feed search in-app (verified results); others deep-link to the providers.
    if (FEEDS[activeKind]) { setFeedQ(q.trim()); return; }
    const list = activeKind ? platforms.filter(p => p.kind === activeKind) : platforms;
    // Deep-link the search into every provider of the active kind (or top 5 overall).
    const winners = activeKind ? list : list.slice(0, 5);
    winners.forEach((p) => window.open(p.search + encodeURIComponent(q.trim()), "_blank", "noopener,noreferrer"));
  };

  const shown = activeKind ? { [activeKind]: grouped[activeKind] || [] } : grouped;

  return (
    <div className="max-w-6xl mx-auto px-4 md:px-6 py-6" data-testid="entertainment-page">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-stone-500 font-bold flex items-center gap-2"><Sparkles size={12} className="text-fuchsia-600" /> Entertainment Hub</div>
          <h1 className="heading text-3xl md:text-4xl font-black text-stone-900">Every app. <span className="text-fuchsia-600">One remote.</span></h1>
          <p className="text-stone-500 text-sm mt-1">Netflix · Prime Video · Hotstar · JioCinema · Zee5 · Sony LIV · MX Player · YouTube · Spotify · JioSaavn · learning platforms and more.</p>
        </div>
        <div className="flex gap-2">
          {[["global","Global"],["in","India"],["us","US"]].map(([k, l]) => (
            <button key={k} onClick={() => setRegion(k)}
                    className={`text-[11px] font-black rounded-full px-3 py-1 ${region === k ? "bg-stone-900 text-white" : "bg-white border border-stone-200 text-stone-700"}`}
                    data-testid={`ent-region-${k}`}>{l}</button>
          ))}
        </div>
      </div>

      <div className="bg-gradient-to-br from-stone-900 to-fuchsia-900 text-white rounded-3xl p-3 flex items-center gap-2 mb-4">
        <MagIcon size={14} className="text-fuchsia-200 ml-2" />
        <input value={q} onChange={(e) => setQ(e.target.value)}
               onKeyDown={(e) => e.key === "Enter" && searchAll()}
               placeholder='Search across all providers — e.g. "Panchayat" or "Coldplay"'
               className="bg-transparent w-full outline-none text-sm placeholder:text-white/50" data-testid="ent-q" />
        <button onClick={searchAll} className="rounded-full bg-white text-fuchsia-900 text-xs font-black px-4 h-9" data-testid="ent-search-btn">
          {FEEDS[activeKind] ? "Search" : `Open in ${activeKind ? "these apps" : "top 5 apps"}`}
        </button>
      </div>

      <div className="flex flex-wrap gap-1.5 mb-4">
        <button onClick={() => { setActiveKind(""); setFeedQ(""); }}
                className={`text-[11px] font-black rounded-full px-3 py-1.5 border ${!activeKind ? "bg-stone-900 text-white border-stone-900" : "bg-white text-stone-700 border-stone-200"}`}
                data-testid="ent-kind-all">All</button>
        {KINDS.map(({ k, l, Icon }) => (
          <button key={k} onClick={() => { setActiveKind(k); setFeedQ(""); }}
                  className={`text-[11px] font-black rounded-full px-3 py-1.5 border inline-flex items-center gap-1.5 ${activeKind === k ? "bg-stone-900 text-white border-stone-900" : "bg-white text-stone-700 border-stone-200"}`}
                  data-testid={`ent-kind-${k}`}>
            <Icon size={12} /> {l}
          </button>
        ))}
      </div>

      {(!activeKind || activeKind === "sports") && <CricketPanel />}
      {FEEDS[activeKind] && <FeedShelf feed={FEEDS[activeKind][0]} title={FEEDS[activeKind][1]} q={feedQ} country={region} />}
      {!activeKind && <FeedShelf feed="shows" title={FEEDS.ott[1]} country={region} limit={12} />}

      <div className="text-[11px] text-stone-500 mb-2" data-testid="ent-links-note">
        Provider links below open each service's own website or app — they are not live data inside find2near.
      </div>
      {loading ? (
        <div className="text-center py-16 text-stone-400"><Loader2 className="mx-auto animate-spin" /></div>
      ) : (
        <div className="space-y-6">
          {KINDS.filter((k) => shown[k.k]?.length).map(({ k, l, Icon, accent }) => (
            <section key={k}>
              <div className="text-[13px] uppercase tracking-widest font-black text-stone-700 mb-2 flex items-center gap-2"><Icon size={14} /> {l}</div>
              <div className="grid gap-3" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))" }}>
                {(shown[k] || []).map((p) => {
                  const logo = logoFor(p.home);
                  return (
                    <a key={p.key} href={p.home} target="_blank" rel="noopener noreferrer"
                       className="group rounded-3xl bg-white border border-stone-200 hover:border-stone-300 hover:shadow-md transition-all p-3 flex flex-col items-center gap-2 relative overflow-hidden"
                       data-testid={`ent-tile-${p.key}`}>
                      <div className={`absolute -inset-16 bg-gradient-to-br ${accent} opacity-0 group-hover:opacity-10 blur-2xl transition-opacity`} />
                      <div className="w-14 h-14 rounded-2xl bg-stone-50 border border-stone-100 grid place-items-center overflow-hidden relative">
                        {logo
                          ? <img src={logo} alt={p.name} className="w-8 h-8" onError={(e) => { e.target.style.display = "none"; }} />
                          : <Icon size={22} className="text-stone-500" />}
                      </div>
                      <div className="text-xs font-black text-stone-900 text-center leading-tight">{p.name}</div>
                      <div className="text-[9px] text-stone-500 inline-flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity"><ExternalLink size={9}/> Open</div>
                    </a>
                  );
                })}
              </div>
            </section>
          ))}
        </div>
      )}

      <div className="mt-8 text-[11px] text-stone-500 text-center">
        We only link to each provider's official website — nothing is streamed through find2near.
      </div>
    </div>
  );
}
